import { ref, type Ref } from 'vue'
import { t } from '@/I18n'
import { NdsButtonColor } from '@/components/NewDesignSystem/NdsButton/NdsButton.interfaces'
import type {
  ConfirmDialogOptions,
  ConfirmDialogState,
  MsgOkDialogOptions,
} from '@/components/NewDesignSystem/NdsConfirmDialog/NdsConfirmDialog.interfaces'

/**
 * Module-level reactive state shared between the composable and NdsConfirmDialog.
 * `null` means no dialog is currently displayed.
 */
const dialogState: Ref<ConfirmDialogState | null> = ref<ConfirmDialogState | null>(null)

const confirm = (options: ConfirmDialogOptions = {}): Promise<void> => {
  if (options.force) {
    return Promise.resolve()
  }

  if (dialogState.value !== null) {
    dialogState.value.reject()
    dialogState.value = null
  }

  return new Promise<void>((resolve, reject) => {
    dialogState.value = {
      message: options.message ?? t('nds_confirm_dialog.are_you_sure'),
      title: options.title ?? '',
      okLabel: options.okLabel ?? t('nds_confirm_dialog.yes'),
      cancelLabel: options.cancelLabel ?? t('nds_confirm_dialog.no'),
      okColor: options.okColor ?? NdsButtonColor.PRIMARY,
      showCloseButton: options.showCloseButton ?? false,
      resolve,
      reject,
    }
  })
}

const msgOk = (options: MsgOkDialogOptions): Promise<void> => {
  if (options.force) {
    return Promise.resolve()
  }

  if (dialogState.value !== null) {
    dialogState.value.reject()
    dialogState.value = null
  }

  return new Promise<void>((resolve, reject) => {
    dialogState.value = {
      message: options.message,
      title: '',
      okLabel: options.okLabel ?? t('nds_confirm_dialog.yes'),
      cancelLabel: null,
      okColor: NdsButtonColor.PRIMARY,
      showCloseButton: false,
      resolve,
      reject,
    }
  })
}

export const useConfirmDialog = () => ({
  dialogState,
  confirm,
  msgOk,
})
